import { SectionStreamActivityRI, StreamActivityNotificationRI } from 'common-utils/rest-interfaces';
import { StreamActivityUpdate } from 'common-utils/interfaces';
import { Observable, forkJoin, of, from } from 'rxjs';
import {pdsLogger} from '../pds-logger';
import {StreamActivityRI} from 'common-utils/rest-interfaces';
import { mapTo } from 'rxjs/operators';
import { GetStreamCacheService } from './get-stream-cache-service';

let feedLog = pdsLogger.requiresLogger('feed');

const ACTIVITY_MESSAGE_PAGE_SIZE = 6; // Six messages per section as per Danny's suggestion.

export class GetStreamFeedService {
  private clientFeed: any;
  private token: string;
  private clientFeedSubscriptions: Array<any> = [];
  private lastActivity: StreamActivityRI | null;

  constructor(private client: any, private groupName: string, private feedName: string, token: string, private isNotificationFeed = false) {
    this.token = token;
    this.clientFeed = this.client.feed(this.groupName, this.feedName, this.token);
    this.debug('New feed', this.clientFeed);
  }

  // Returns an Observable that can emit responses multiple times
  realTimeUpdates(): Observable<StreamActivityUpdate> {
    return Observable.create((observer) => {

      let connectionSuccessCallback = () => {
        this.debug('Listening in realtime for ' + this.feedInfo());
      };

      let connectionFailedCallback = (data) => {
        observer.complete();
        throw new Error('Getstream: something went wrong when initializing real-time updates connection for ' + this.feedInfo());
      };

      let realTimeUpdatesCallback = (data) => {
        // new activity added
        if (data.new && data.new.length > 0) {
          GetStreamCacheService.evictObservable(this.clientFeed.feedUrl, 'GetStreamFeedService.realTimeUpdates (new activity)');
          for (let newActivity of data.new) {
            observer.next({
              newActivity: newActivity
            });
          }
        }

        // Activity deleted
        if (data.deleted && data.deleted.length > 0) {
          GetStreamCacheService.evictObservable(this.clientFeed.feedUrl, 'GetStreamFeedService.realTimeUpdates (deleted activity)');
          for (let deletedActivity of data.deleted) {
            observer.next({
              deletedActivity: {
                id: deletedActivity,
                // When logged in as teacher, data.feed is in the form "section_feeds:SECTION-11E74D41-6C56-1C46-B42B-010011AB0000"
                // When logged in as parent or teacher, data.feed is in the form "student_feeds:STUDENT-11E74D41-5C57-6F12-B42B-010011AB0000"
                feed: data.feed
              }
            });
          }
        }
      };

      // subscribe actually returns a promise
      let subscription = this.clientFeed.subscribe(realTimeUpdatesCallback);

      // Need to track these subscriptions so that we can cancel them.
      this.clientFeedSubscriptions.push(subscription);

      subscription.then(connectionSuccessCallback, connectionFailedCallback);
    });
  }

  cancelSubscriptions(): void {
    for (let subscription of this.clientFeedSubscriptions) {
      subscription.cancel();
    }
    this.clientFeedSubscriptions = [];
  }

  getFeedId(): string {
    return this.clientFeed.id;
  }

  getSignedId(): string {
    return this.clientFeed.id + ' ' + this.clientFeed.token;
  }

  get hasMoreActivities(): boolean {
    return this.lastActivity !== null;
  }

  getPreviousActivities(limit = ACTIVITY_MESSAGE_PAGE_SIZE): Observable<Array<StreamActivityRI>> {
    if (this.isNotificationFeed) {
      throw 'can not fetch activities on notification feed yet';
    }

    let getFeedObservable = (options: { id_lt?: string, limit: number }): Observable<any> => {
      return from(
        // Important:
        // - We need the feed.get promise resolved right away by calling the then() function so that the
        //   we can get the resulting data to cache along with the observable.
        this.clientFeed.get(options).then(
          (res: { duration: string, next: string, results: Array<StreamActivityRI> }) => {
            if (res.results.length > 0) {
              this.lastActivity = res.results[res.results.length - 1];
            } else {
              this.lastActivity = null;
            }
            return res.results;
          }
        ).catch((err: any) => {
          this.debug('[getPreviousActivities]', err);
        })
      );
    };

    if (this.lastActivity != null) {
      // Don't bother caching this is additional data on top of first read.
      // This gets called when users click "More Messages".
      return getFeedObservable({ id_lt: this.lastActivity.id, limit: limit });
    } else {
      let observable = GetStreamCacheService.getObservable(this.clientFeed.feedUrl, limit);
      if (observable == null) {
        observable = getFeedObservable({ limit: limit });
        GetStreamCacheService.cacheObservable(this.clientFeed.feedUrl, limit, 'GetStreamFeedService.getPreviousActivities', observable);
      }
      return observable;
    }
  }

  getNotifications(limit = 200): Observable<Array<StreamActivityNotificationRI>> {
    if (!this.isNotificationFeed) {
      throw 'can only get notifications from notification feeds';
    }

    let observable = GetStreamCacheService.getObservable(this.clientFeed.feedUrl, limit);
    if (observable == null) {
      observable = from(
        // Important:
        // - We need the feed.get promise resolved right away by calling the then() function so that the
        //   we can get the resulting data to cache along with the observable.
        this.clientFeed.get({ limit: limit }).then(
          (res: { duration: string, next: string, results: Array<StreamActivityNotificationRI> }) => res.results
        )
      );
      GetStreamCacheService.cacheObservable(this.clientFeed.feedUrl, limit, 'GetStreamFeedService.getNotifications', observable);
    }
    return observable;
  }

  markNotificationsRead(getStreamGroupIds: Array<string>): Observable<null> {
    if (!this.isNotificationFeed) {
      throw 'can only mark notifications read on notification feeds';
    }

    if (getStreamGroupIds.length === 0) {
      return of(null);
    }

    let ids = JSON.parse(JSON.stringify(getStreamGroupIds)); // avoid outter scope mutation
    let obs: Array<Observable<any>> = [];
    /**
     * Urls have a length limit of 2000 characters, which we could exceed given enough notifications.
     * There are 200 chars from the api (164 + buffer), so we splice the first 45 uids from the ids
     * which is approximately 1800 characters (1755 + buffer) to ensure we don't exceed the url limit.
     */
    // Since we are updating the read status, evict the related cache.
    GetStreamCacheService.evictObservable(this.clientFeed.feedUrl, 'GetStreamFeedService.markNotificationsRead');

    while (ids.length > 0) {
      let batch = ids.splice(0, 45);
      obs.push(this.clientFeed.get({ limit: 5, mark_read: batch }));
    }
    return forkJoin(obs).pipe(mapTo(null));
  }

  getFeedName(): string {
    return this.feedName;
  }

  private debug(msg: string, ...params: Array<any>): void {
    feedLog('Getstream(' + this.feedInfo() + '): ' + msg, params);
  }

  private feedInfo(): string {
    return this.groupName + ': ' + this.feedName;
  }
}
